import './App.css';
import React, { useRef, useEffect, useCallback } from 'react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { IconButton, styled } from '@mui/material';
import { SnackbarProvider as Snackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useCurrentTheme from './Theme/useCurrentTheme';
import { intializeAxios } from './utils/Axios';
import { ReactComponent as CloseButton } from './assets/closeButtonWhite.svg';
import AppRoutes from './routing/AppRoutes';
import FullscreenLoader from './components/Common/FullscreenLoader';

const SnackbarProvider = styled(Snackbar)`
	&.SnackbarItem-contentRoot{
		width: 307px;
	}
	& .SnackbarItem-message{
		margin-right: 27px;
    word-break: break-word;
	}
	& .SnackbarItem-action{
		position: absolute;
		right: 0;
		top: 5px;
		margin-right: 5px;
		padding: 0;
	}
    &.SnackbarItem-variantSuccess {
        background: ${(p) => p.theme.palette.success.main};
    },
	&.SnackbarItem-variantError {
		background: ${(p) => p.theme.palette.error.main};
	},
	&.SnackbarItem-variantWarning {
		background: ${(p) => p.theme.palette.warning.main};
	},
	&.SnackbarItem-variantInfo {
		background: ${(p) => p.theme.palette.info.main};
	},
`;
intializeAxios();

const params = new URLSearchParams(window.location.search);
if (params.get('clearLocalStorage') === 'true') {
  localStorage.clear();
  window.history.replaceState({}, document.title, window.location.pathname);
}

function App() {
  const userTheme = useSelector((state) => state.common.userTheme);
  const notistackRef = useRef();
  const theme = useCurrentTheme(`${userTheme}-theme`);

  useEffect(() => {
    window.enqueueSnackbar = notistackRef.current.enqueueSnackbar;
    window.closeSnackbar = notistackRef.current.closeSnackbar;
  }, []);

  const onClickSnackBarClose = useCallback((key) => {
    notistackRef.current.closeSnackbar(key);
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          ref={notistackRef}
          maxSnack={3}
          hideIconVariant
          preventDuplicate
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          autoHideDuration={3000}
          action={(key) => (
            <SnackbarAction
              snackbarKey={key}
              onClickSnackBarClose={onClickSnackBarClose}
            />
          )}
        >
          <FullscreenLoader />
          <AppRoutes />
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

const SnackbarAction = ({ onClickSnackBarClose, snackbarKey }) => {
  return (
    <IconButton onClick={() => onClickSnackBarClose(snackbarKey)}>
      <CloseButton color="#ffffff" />
    </IconButton>
  );
};

SnackbarAction.propTypes = {
  onClickSnackBarClose: PropTypes.func.isRequired,
  snackbarKey: PropTypes.string.isRequired,
};

export default App;
